import React, {ReactElement} from 'react';
import {jwtDecode} from 'jwt-decode';
import moment from 'moment';
import {
  Box,
  Typography,
  useTheme,
  Checkbox,
  IconButton,
  Stack,
  Card,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineContent,
  TimelineConnector,
  timelineOppositeContentClasses,
  TimelineOppositeContent
} from '@mui/lab';
import LoadingButton from '@mui/lab/LoadingButton';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';
import {
  GridColDef,
  useGridApiRef,
  GridEditSingleSelectCell,
  GridEditInputCell,
  GridToolbarColumnsButton
} from '@mui/x-data-grid-premium';
import {useSnackbar} from 'notistack';
import {DatePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import FormControl from '@mui/material/FormControl';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';
import CustomPagination from '../table-custom-pagination/TableCustomPagination';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import {NoRowsOverlay, GridCellRender} from '../../grid-utils';
import BatchListing from '../batch-listing/BatchListing';
import BatchLedger from '../batch-ledger/BatchLedger';
import Dialog, {dialogClasses} from '@mui/material/Dialog';
import SelectInput from '@components/form/select-input/SelectInput';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import PaymentEntry from '../payment-entry/PaymentEntry';

import {
  PaymentAutoPostWrapper,
  VisuallyHiddenInput,
  FlexContainer,
  FlexGridCell
} from './PaymentAutoPostStyles';

import {rowStyles} from '@shared/utils/HelperUtils';
import AuthUtils from 'src/utils/AuthUtils';
import {fCurrency} from 'src/utils/format-number';
import EncryptUtils from 'src/utils/EncryptUtils';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import {APP} from 'src/styles/variables';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {
  deepCopy,
  getCurrentMonthStartDate,
  getCurrentMonthEndDate,
  getGridSx,
  getApplicationName
} from '../../util';

import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {useBoolean} from 'src/hooks/use-boolean';

import {useAppContext} from '@context/AppContext';

const headerTypographySx = {
  fontSize: '16px !important',
  fontWeight: 700
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const PaymentAutoPost = (props: any) => {
  let userData: any, userEmail: any;
  const theme = useTheme();
  const gridApiRef = useGridApiRef();
  const batchSummaryList = useBoolean();
  const eraList = useBoolean();
  const {enqueueSnackbar} = useSnackbar();
  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
    userEmail = userData?.email || userData?.user?.email;
  }
  // File upload grid state variables
  const [file, setFile] = React.useState<File | null>(null);
  const [errorFileAlert, setErrorFileAlert] = React.useState<boolean>(false);
  const [openRemoveTransactionDialog, setOpenTransactionRemoveDialog] =
    React.useState<boolean>(false);
  const [uploadingStatus, setUploadingStatus] = React.useState(false);

  // Accepted ERAs/Transactions grid state variables
  const [acceptedTransactions, setAcceptedTransactions] = React.useState<any>({
    transactions: [],
    totalPaymentAmountSum: 0,
    pageCount: 1,
    totalRecordsCount: 0
  });
  const [
    acceptedTransactionsTableLoading,
    setAcceptedTransactionsTableLoading
  ] = React.useState(false);
  const [transactionToRemove, setTransactionToRemove] = React.useState<any>({
    batchId: null,
    fileName: ''
  });
  const [transactionsSortBy, setTransactionsSortBy] = React.useState<any>([]);
  const [acceptedTransactionsFilters, setAcceptedTransactionsFilters] =
    React.useState<any>({
      items: [],
      logicOperator: 'AND'
    });
  // Accepted transactions pagination setup
  const [transactionsPage, setTransactionsPage] = React.useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = React.useState(25);
  const [transactionsPageCount, setTransactionsPageCount] = React.useState(1);
  const [transactionsTotalRecordsCount, setTransactionsTotalRecordsCount] =
    React.useState(1);

  const [claimsTransactionId, setClaimsTransactionId] =
    React.useState<any>(null);
  const [selectedClaimTransactionIds, setSelectedClaimTransactionIds] =
    React.useState<any>([]);
  const [reviewTransactionsLoading, setReviewTransactionsLoading] =
    React.useState(false);
  const [usersInfo, setUsersInfo] = React.useState<any>([]);
  const [startDate, setStartDate] = React.useState<any>(
    getCurrentMonthStartDate()
  );
  const [endDate, setEndDate] = React.useState<any>(getCurrentMonthEndDate());
  const [summaryTableLoading, setSummaryTableLoading] =
    React.useState<any>(false);
  const [validDateStatus, setValidDateStatus] = React.useState(false);
  const [summaryData, setSummaryData] = React.useState<any>([]);
  const [displayPostERAModal, setDisplayPostERAModal] =
    React.useState<any>(false);
  const [eraRecordToPost, setERARecordToPost] = React.useState<any>({});
  const [postERALoading, setPostERALoading] = React.useState(false);

  const [newRemark, setNewRemark] = React.useState('');
  const [remarksAddButtonLoading, setRemarksAddButtonLoading] =
    React.useState(false);

  const [isRemarksDialogOpen, setIsRemarksDialogOpen] = React.useState(false);
  const [remarksBatchId, setRemarksBatchId] = React.useState<any>('');
  const [remarksHistoryLoading, setRemarksHistoryLoading] =
    React.useState(false);
  const [remarksHistory, setRemarksHistory] = React.useState<any>([]);
  const [batchLedgerId, setBatchLedgerId] = React.useState(null);
  const [isPageLoading, setIsPageLoading] = React.useState(false);
  const [legacyApps, setLegacyApps] = React.useState<any>([]);
  const [selectedLegacyApp, setSelectedLegacyApp] = React.useState('');
  const [isPaymentEntry, setIsPaymentEntry] = React.useState(false);
  const [paymentEntryBatchId, setPaymentEntryBatchId] =
    React.useState<any>(null);
  const [removeBatchLoading, setRemoveBatchLoading] = React.useState(false);
  const [shouldDisplayEditIcon, setShouldDisplayEditIcon] =
    React.useState(false);

  const {
    appState: {applicationName}
  } = useAppContext();

  const isMounted = React.useRef(false);

  const isActionDisabled = (params: any) => {
    if (params.row.PostingStatus === 'Posted') {
      return true;
    }
    if (props?.patientAccountingConfig?.super_user) {
      return false;
    }
    if (
      params.row.AssignToUserEmail === userEmail ||
      params.row.CreatedByUserEmail === userEmail
    ) {
      return false;
    }
    return true;
  };

  const isRemarksDisabled = (params: any) => {
    if (props?.patientAccountingConfig?.super_user) {
      return false;
    }
    if (
      params.row.AssignToUserEmail === userEmail ||
      params.row.CreatedByUserEmail === userEmail
    ) {
      return false;
    }
    return true;
  };

  const isSecuredUserDisabled = (params: any) => {
    if (params.row.PostingStatus === 'Posted') {
      return true;
    }
    if (props?.patientAccountingConfig?.super_user) {
      return false;
    }
    if (params.row.CreatedByUserEmail === userEmail) {
      return false;
    }
    return true;
  };

  const getAcceptedTransactions = async (shouldGridLoad = true) => {
    const reqPayload = {
      sortBy: transactionsSortBy,
      filterBy: acceptedTransactionsFilters,
      limit: transactionsPageSize,
      offset: (transactionsPage - 1) * transactionsPageSize,
      searchForm: props?.patientAccountingConfig
    };
    const tk = EncryptUtils.encryptedData(reqPayload);
    if (shouldGridLoad) {
      setAcceptedTransactionsTableLoading(true);
    }
    try {
      const acceptedTransactionsData = await APIService.getAcceptedTransactions(
        {
          tk
        }
      );
      setAcceptedTransactions({...acceptedTransactionsData.data});
      setTransactionsPageCount(acceptedTransactionsData.data.pageCount);
      setTransactionsTotalRecordsCount(
        acceptedTransactionsData.data.totalRecordsCount
      );
      setAcceptedTransactionsTableLoading(false);
    } catch (e) {
      setAcceptedTransactionsTableLoading(false);
    }
  };

  const getRCMLegacyApps = async () => {
    setIsPageLoading(true);
    try {
      const tk = EncryptUtils.encryptedData({
        paymentConfig: props?.paymentConfig
      });
      const legacyAppData = await APIService.getRCMLegacyApps({tk});
      setLegacyApps(legacyAppData.data);
      setIsPageLoading(false);
    } catch (e) {
      setIsPageLoading(false);
    }
  };

  const getBytepadUsers = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        paymentConfig: props?.paymentConfig,
        applicationName: applicationName
      });
      const usersInfoData = await APIService.getBytepadUsers({tk});
      setUsersInfo([...usersInfoData.data]);
    } catch (e) {
      return;
    }
  };

  const getERAStatusSummary = async (start: any, end: any) => {
    const reqPayload = {
      startDate: start
        ? dayjs(start).startOf('day').format('YYYY-MM-DD HH:mm:ss')
        : '',
      endDate: end ? dayjs(end).endOf('day').format('YYYY-MM-DD HH:mm:ss') : '',
      searchForm: props?.patientAccountingConfig,
      batchType: 'auto_post'
    };
    try {
      setSummaryTableLoading(true);
      const tk = EncryptUtils.encryptedData(reqPayload);
      const summaryStatus = await APIService.getBatchSummary({tk});
      setSummaryData([...summaryStatus.data]);
      setSummaryTableLoading(false);
    } catch (e) {
      setSummaryTableLoading(false);
    }
  };

  const initApiCalls = async () => {
    getRCMLegacyApps();
    await getBytepadUsers();
    getAcceptedTransactions();
    getERAStatusSummary(startDate, endDate);
  };

  const getRemarksHistory = async (batchId: any) => {
    try {
      setRemarksHistoryLoading(true);
      const tk = EncryptUtils.encryptedData({
        batchId: batchId,
        paymentConfig: props?.paymentConfig
      });
      const res = await APIService.getBatchRemarks({tk});

      setRemarksHistoryLoading(false);
      setRemarksHistory(res.data);
    } catch (error) {
      setRemarksHistoryLoading(false);
    }
  };

  const init = () => {
    initApiCalls();
    return;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      event.target.value = '';
    }
  };

  const proceedWithUpload = async () => {
    setErrorFileAlert(false);
    if (!file) return;
    const reqPayload = EncryptUtils.encryptedData({
      userEmail: userEmail,
      legacyApp: selectedLegacyApp,
      paymentConfig: props?.paymentConfig
    });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tk', reqPayload);
    setUploadingStatus(true);
    try {
      await APIService.uploadERAFile(formData);
      setUploadingStatus(false);
      setFile(null);
      await initApiCalls();
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setUploadingStatus(false);
      setFile(null);
    }
  };

  const onUploadClick = () => {
    if (!file) return;
    if (!file || file.size < 1024) {
      setErrorFileAlert(true);
    } else {
      proceedWithUpload();
    }
  };

  const onErrorFileAlertClose = () => {
    setErrorFileAlert(false);
    setFile(null);
  };

  const onUploadYesClick = () => {
    proceedWithUpload();
  };

  const onRefreshTransactions = async () => {
    getAcceptedTransactions();
  };

  const updateBatchDetails = async (
    batchId: any,
    batchDetails: any,
    shouldReloadBatch = true
  ) => {
    const tk = EncryptUtils.encryptedData({
      batchId: batchId,
      batchDetails: batchDetails,
      paymentConfig: props?.paymentConfig
    });
    try {
      await APIService.updateBatchDetails({tk});
      if (shouldReloadBatch) {
        onRefreshTransactions();
      }
    } catch (e) {}
  };

  const onRemoveTransaction = async () => {
    const tk = EncryptUtils.encryptedData({
      batchId: transactionToRemove.batchId,
      paymentConfig: props?.paymentConfig
    });
    try {
      setRemoveBatchLoading(true);
      await APIService.removeBatch({tk});
      setRemoveBatchLoading(false);
      setTransactionToRemove({
        batchId: null,
        fileName: null
      });
      onRefreshTransactions();
    } catch (e) {
      setRemoveBatchLoading(false);
    }
  };

  const onCancelRemovePostEra = () => {
    setDisplayPostERAModal(false);
    setERARecordToPost({});
  };

  const postERA = async () => {
    try {
      const acceptedTransactionsCopy = deepCopy(acceptedTransactions);
      const trIndex = acceptedTransactionsCopy.transactions.findIndex(
        (tr: any) => tr.id === eraRecordToPost.id
      );
      if (trIndex === -1) {
        return;
      }
      const postedFileName =
        acceptedTransactionsCopy.transactions[trIndex].fileName;
      setPostERALoading(true);
      const updatedDetails = [
        {name: 'PostingStatus', value: 'Posted'},
        {name: 'PostingStatusReason', value: ''}
      ];
      await updateBatchDetails(eraRecordToPost.id, updatedDetails, false);
      setPostERALoading(false);
      onCancelRemovePostEra();
      enqueueSnackbar(
        `${postedFileName} POSTED SUCCESSFULLY`,
        SnackBarConfig.getSuccess()
      );
      getAcceptedTransactions(false);
      getERAStatusSummary(startDate, endDate);
    } catch (e) {
      setAcceptedTransactionsTableLoading(false);
    }
  };

  const onSortModelChange = (sortModel: any) => {
    if (sortModel) {
      setTransactionsSortBy([...sortModel]);
    }
  };

  const handleDeleteButtonClick = (params: any) => {
    setOpenTransactionRemoveDialog(true);
    const batchId = params.row.id;
    setTransactionToRemove({
      batchId: batchId,
      fileName: params.row.FileName
    });
  };

  const handleRunButtonClick = async (params: any) => {
    try {
      const tk = EncryptUtils.encryptedData({
        batchId: params.id,
        userEmail: userEmail,
        eraTransactionId: params.row.eraTransactionId,
        legacyApp: params.row.LegacyApp
      });
      await APIService.runERA({tk});
      getAcceptedTransactions(false);
      getERAStatusSummary(startDate, endDate);
    } catch (e) {
      setAcceptedTransactionsTableLoading(false);
    }
  };

  const handlePostButtonClick = (params: any) => {
    setDisplayPostERAModal(true);
    setERARecordToPost({...params.row});
  };

  const handleViewButtonClick = (params: any) => {
    const transactionId = params.row.eraTransactionId;
    setClaimsTransactionId(transactionId);
  };

  const handleBatchLedgerClick = (params: any) => {
    setBatchLedgerId(params.row.id);
  };

  const handleBatchPaymentEntryClick = (params: any) => {
    setPaymentEntryBatchId(params.row.id);
    setIsPaymentEntry(true);
  };

  const handleBooleanCheckboxChange = async (e: any, params: any) => {
    const updateDetails = [
      {name: 'BankConfirmation', value: e.target.checked ? 1 : 0}
    ];
    updateBatchDetails(params.row.id, updateDetails);
  };

  const handleBBPBooleanCheckboxChange = async (e: any, params: any) => {
    const updateDetails = [
      {name: 'IsBbpBatch', value: e.target.checked ? 1 : 0}
    ];
    updateBatchDetails(params.row.id, updateDetails);
  };

  const processRowUpdate = async (newRow: any, oldRow: any) => {
    if (
      newRow.AssignToUserName &&
      newRow.AssignToUserName !== oldRow.AssignToUserName &&
      newRow.AssignToUserName !== oldRow.AssignToUser
    ) {
      const updateDetails = [
        {name: 'AssignToUser', value: newRow.AssignToUserName}
      ];
      const assignToUserObj = usersInfo.find(
        (u: any) => u.id === newRow.AssignToUserName
      );
      newRow.AssignToUserName = assignToUserObj ? assignToUserObj.fullName : '';
      newRow.AssignToUser = newRow.AssignToUserName;
      updateBatchDetails(newRow.id, updateDetails);
    } else if (newRow.BatchName && newRow.BatchName !== oldRow.BatchName) {
      const updateDetails = [{name: 'BatchName', value: newRow.BatchName}];
      updateBatchDetails(newRow.id, updateDetails);
    }
    return newRow;
  };

  const onCancelRemoveTransactionClick = () => {
    setOpenTransactionRemoveDialog(false);
    setTransactionToRemove({
      batchId: '',
      fileName: ''
    });
  };

  const isCellEditable = (params: any) => {
    if (['BankConfirmation', 'BatchName'].includes(params.field)) {
      return params.row.PostingStatus === 'Not Posted';
    } else if (params.field === 'AssignToUserName') {
      return isSecuredUserDisabled(params) ? false : true;
    }
    return false;
  };

  const isRowSelectable = (params: any) => {
    if (params.row.id === 'summary_record') return false;
    if (params.row.reviewed) return false;
    if (['Posted'].includes(params.row.PostingStatus)) return true;
    return false;
  };

  const onRowSelectionModelChange = (selectionModel: any) => {
    setSelectedClaimTransactionIds([...selectionModel]);
  };

  const getCellClassName = (params: any) => {
    return ['AssignToUserName', 'BatchName'].includes(params.field)
      ? 'autopost-grid-editable-cell'
      : '';
  };

  const getActionsRunDisabledStatus = (params: any) => {
    if (!params.row.BankConfirmation) return true;
    if (params.row.PostingStatus !== 'Not Posted') return true;
    return false;
  };

  const getPostingStatusFontColor = (status: string) => {
    if (['Posted', 'Balanced'].includes(status)) return 'success.main';
    else if (status === 'In Progress') return '#FFBF00';
    else if (status === 'Unprocessed') return 'error.main';
    else return 'black';
  };

  const getActionsPostDisabledStatus = (params: any) => {
    return params.row.PostingStatus !== 'Balanced';
  };

  const getActionsViewDisabledStatus = (params: any) => {
    if (
      ['Posted', 'In Progress', 'Not Posted'].includes(params.row.PostingStatus)
    ) {
      return true;
    }
    return params.row.BankConfirmation ? false : true;
  };

  const getActionsBatchLedgerDisabledStatus = (params: any) => {
    if (['Balanced', 'Unprocessed'].includes(params.row.PostingStatus)) {
      return false;
    }
    return true;
  };

  const getActionsDeleteDisabledStatus = (params: any) => {
    if (['Posted', 'In Progress'].includes(params.row.PostingStatus)) {
      return true;
    }
    return false;
  };

  const getBankConfirmationDisabledStatus = (params: any) => {
    if (params.row.PostingStatus === 'Not Posted') return false;
    return true;
  };

  const getBBPBatchEnabledStatus = (params: any) => {
    if (params.row.PostingStatus === 'Posted') return true;
    return false;
  };

  const onReviewTransactions = async () => {
    if (!selectedClaimTransactionIds.length) return;
    setReviewTransactionsLoading(true);
    try {
      const tk = EncryptUtils.encryptedData({
        batchIds: selectedClaimTransactionIds,
        paymentConfig: props?.paymentConfig
      });
      await APIService.markBatchesReviewed({tk});
      getAcceptedTransactions();
      setReviewTransactionsLoading(false);
    } catch (e) {
      setReviewTransactionsLoading(false);
    }
  };

  const onChangeStartDate = (e: any) => {
    setStartDate(e);
  };

  const onChangeEndDate = (e: any) => {
    setEndDate(e);
  };

  const onApplyButtonClick = async () => {
    if (!validDateStatus) return;
    getERAStatusSummary(startDate, endDate);
  };

  const onClearFilters = async () => {
    setStartDate(null);
    setEndDate(null);
    getERAStatusSummary(null, null);
  };

  const updateValidDateStatus = (startDate1: any, endDate1: any) => {
    const start = startDate1 ? dayjs(startDate1) : null;
    const end = endDate1 ? dayjs(endDate1) : null;
    if (!start && !end) {
      setValidDateStatus(false);
      return;
    } else if (start && !start.isValid()) {
      setValidDateStatus(false);
      return;
    } else if (end && !end.isValid()) {
      setValidDateStatus(false);
      return;
    }
    if (end && start && end.isBefore(start)) {
      setValidDateStatus(false);
      return;
    }
    setValidDateStatus(true);
  };

  const handleCloseDialog = () => {
    setIsRemarksDialogOpen(false);
    setRemarksBatchId(null);
    setRemarksHistory([]);
  };

  const handleAddRemark = async () => {
    if (!newRemark.trim()) return;
    try {
      setRemarksAddButtonLoading(true);
      const tk = EncryptUtils.encryptedData({
        batchId: remarksBatchId || '',
        remarks: newRemark.trim(),
        paymentConfig: props?.paymentConfig
      });
      const res = await APIService.addBatchRemarks({tk});
      enqueueSnackbar(res.data.message, SnackBarConfig.getSuccess());
      getAcceptedTransactions();

      setNewRemark('');
      setRemarksAddButtonLoading(false);
      setIsRemarksDialogOpen(false);
      setRemarksBatchId(null);
    } catch (e) {
      setRemarksAddButtonLoading(false);
    }
  };

  const handleAddRemarkClick = (batchId: string) => {
    setRemarksBatchId(batchId);
    setIsRemarksDialogOpen(true);
    getRemarksHistory(batchId);
  };

  const getUsersOptionsForSingleSelect = () => {
    return usersInfo
      ? usersInfo.map((u: any) => ({
          label: u.fullName,
          value: u.id
        }))
      : [];
  };

  const onChangeLegacyApp = (e: any) => {
    setSelectedLegacyApp(e.target.value);
  };

  const onClosePaymentEntry = () => {
    setPaymentEntryBatchId(null);
    setIsPaymentEntry(false);
  };

  const handleMarkCompleteCheckboxChange = (id: number) => {
    selectedClaimTransactionIds((prev: any) =>
      prev.includes(id)
        ? prev.filter((rowId: any) => rowId !== id)
        : [...prev, id]
    );
  };

  React.useEffect(() => {
    updateValidDateStatus(startDate, endDate);
  }, [startDate, endDate]);

  React.useEffect(() => {
    init();
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isMounted.current) {
      getAcceptedTransactions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    acceptedTransactionsFilters,
    transactionsSortBy,
    transactionsPage,
    transactionsPageSize
  ]);

  const Pagination = () => {
    return (
      <CustomPagination
        page={transactionsPage}
        pageSize={transactionsPageSize}
        rowsPerPageOptions={[25, 50, 100]}
        pageCount={transactionsPageCount}
        totalRecordsCount={transactionsTotalRecordsCount}
        totalRows={acceptedTransactions.transactions.length}
        setPage={setTransactionsPage}
        setPageSizes={setTransactionsPageSize}
      />
    );
  };

  const acceptedTransactionColumns: GridColDef[] = [
    {
      field: 'selection',
      headerName: 'Mark Reviewed',
      sortable: false,
      filterable: false,
      width: 100,
      renderCell: (params: any) => (
        <Checkbox
          disabled={!isRowSelectable(params)}
          color="success"
          checked={selectedClaimTransactionIds.includes(params.row.id)}
          onChange={() => handleMarkCompleteCheckboxChange(params.row.id)}
        />
      ),
      colSpan: (params: any) => (params.row.id === 'summary_record' ? 10 : 0)
    },
    {
      field: 's_no',
      headerName: 'S.No',
      type: 'number',
      sortable: false,
      filterable: false,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.row.id === 'summary_record'
              ? ''
              : params.api.getAllRowIds().indexOf(params.id) + 1
          }
        />
      )
    },
    {
      field: 'LegacyApp',
      headerName: 'Legacy App',
      type: 'string',
      flex: 0,
      width: 130,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'BytepadCreatedDate',
      headerName: 'Uploaded On',
      type: 'date',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'UploadedBy',
      headerName: 'Uploaded By',
      type: 'string',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'productionDate',
      headerName: 'Received On',
      type: 'date',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'FileName',
      headerName: 'ERA File Name',
      type: 'string',
      flex: 0,
      width: 120,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'batchId',
      type: 'number',
      headerName: 'ERA Batch ID',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'BatchName',
      headerName: 'Batch Name',
      flex: 0,
      width: 120,
      headerAlign: 'left',
      align: 'left',
      editable: true,
      renderCell: (params: any) =>
        shouldDisplayEditIcon ? (
          <FlexGridCell>
            <GridCellRender value={params.value} />
            <IconButton
              sx={{color: 'success.main'}}
              disabled={params.row.PostingStatus !== 'Not Posted'}
              size="small"
              onClick={() =>
                params.api.startCellEditMode({
                  id: params.id,
                  field: params.field
                })
              }>
              <EditIcon fontSize="small" />
            </IconButton>
          </FlexGridCell>
        ) : (
          <GridCellRender value={params.value} />
        ),
      renderEditCell: (params: any) => <GridEditInputCell {...params} />
    },
    {
      field: 'checkOrEftTraceNumber',
      headerName: 'EFT/Check Number',
      filterable: false,
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'paymentDate',
      headerName: 'Deposit Date',
      type: 'date',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'BankConfirmation',
      headerName: 'Bank Confirmation',
      type: 'boolean',
      flex: 0,
      width: 120,
      headerAlign: 'left',
      align: 'center',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <Checkbox
            disabled={getBankConfirmationDisabledStatus(params)}
            checked={params.value}
            color="success"
            onChange={(e: any) => handleBooleanCheckboxChange(e, params)}
          />
        ) : (
          <GridCellRender value="Total" typographySx={{fontWeight: 'bold'}} />
        )
    },
    {
      field: 'TotalPaymentReceived',
      headerName: 'Total Payment Received',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      type: 'number',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <GridCellRender value={fCurrency(params.value)} />
        ) : (
          <GridCellRender
            value={fCurrency(params.row.sumValue)}
            typographySx={{fontWeight: 'bold'}}
          />
        )
    },
    {
      field: 'TotalBatchAmountPosted',
      headerName: 'Total Payment Posted',
      type: 'number',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      ),
      colSpan: (params: any) => (params.row.id === 'summary_record' ? 10 : 0)
    },
    {
      field: 'TotalVariance',
      headerName: 'Variance',
      type: 'number',
      flex: 0,
      width: 100,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => {
        const varianceAmount =
          params.row.TotalPaymentReceived - params.row.TotalPaymentPosted;
        return <GridCellRender value={fCurrency(varianceAmount)} />;
      }
    },
    {
      field: 'AssignToUserName',
      headerName: 'Assign To',
      type: 'singleSelect',
      editable: true,
      valueOptions: getUsersOptionsForSingleSelect(),
      flex: 0,
      width: 120,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'actions',
      renderCell: (params: any) =>
        shouldDisplayEditIcon ? (
          <FlexGridCell>
            <GridCellRender value={params.value} />
            <IconButton
              sx={{color: 'success.main'}}
              disabled={params.row.PostingStatus !== 'Not Posted'}
              size="small"
              onClick={() =>
                params.api.startCellEditMode({
                  id: params.id,
                  field: params.field
                })
              }>
              <EditIcon fontSize="small" />
            </IconButton>
          </FlexGridCell>
        ) : (
          <GridCellRender value={params.value} />
        ),
      renderEditCell: (params: any) => (
        <GridEditSingleSelectCell
          {...params}
          valueOptions={getUsersOptionsForSingleSelect()}
        />
      )
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      filterable: false,
      flex: 0,
      width: 200,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 'calc(100% - 50px)'
              }}>
              {params.value}
            </Typography>
            <LoadingButton
              variant="outlined"
              disabled={isRemarksDisabled(params)}
              onClick={() => handleAddRemarkClick(params.row.id)}
              sx={{
                minWidth: 'unset',
                padding: '0px 8px',
                fontSize: '12px',
                color: theme.palette.primary.main
              }}>
              View
            </LoadingButton>
          </Box>
        );
      }
    },
    {
      field: 'IsBbpBatch',
      headerName: 'BBP Batch Enabled',
      type: 'boolean',
      flex: 0,
      width: 120,
      headerAlign: 'left',
      align: 'center',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <Checkbox
            disabled={getBBPBatchEnabledStatus(params)}
            checked={params.value}
            color="success"
            onChange={(e: any) => handleBBPBooleanCheckboxChange(e, params)}
          />
        ) : (
          <GridCellRender value="Total" typographySx={{fontWeight: 'bold'}} />
        )
    },
    {
      field: 'PostingStatus',
      headerName: 'Status',
      type: 'string',
      flex: 0,
      width: 120,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender
          value={params.value}
          typographySx={{
            color: getPostingStatusFontColor(params.value),
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 600,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => (
        <Box sx={{display: 'flex', gap: 1, width: '100%'}}>
          <LoadingButton
            type="submit"
            fullWidth
            size="small"
            variant="contained"
            sx={{
              backgroundColor: 'success.main'
            }}
            disabled={
              getActionsRunDisabledStatus(params) || isActionDisabled(params)
            }
            onClick={() => handleRunButtonClick(params)}>
            Run
          </LoadingButton>
          <LoadingButton
            type="submit"
            fullWidth
            size="small"
            variant="contained"
            sx={{
              backgroundColor: 'success.main'
            }}
            disabled={
              getActionsPostDisabledStatus(params) || isActionDisabled(params)
            }
            onClick={() => handlePostButtonClick(params)}>
            Post
          </LoadingButton>
          <LoadingButton
            type="submit"
            fullWidth
            size="small"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.dark
            }}
            disabled={
              getActionsViewDisabledStatus(params) || isActionDisabled(params)
            }
            onClick={() => handleViewButtonClick(params)}>
            Batch List
          </LoadingButton>
          <LoadingButton
            type="submit"
            variant="outlined"
            fullWidth
            size="small"
            sx={{
              backgroundColor: theme.palette.primary.light,
              minWidth: 'unset'
            }}
            disabled={
              getActionsBatchLedgerDisabledStatus(params) ||
              isActionDisabled(params)
            }
            onClick={() => handleBatchLedgerClick(params)}>
            Transaction List
          </LoadingButton>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth
            size="small"
            sx={{
              backgroundColor: theme.palette.primary.darker,
              minWidth: 'unset'
            }}
            disabled={
              params.row.PostingStatus === 'Posted' || isActionDisabled(params)
            }
            onClick={() => handleBatchPaymentEntryClick(params)}>
            Payment Entry
          </LoadingButton>
          <LoadingButton
            type="submit"
            fullWidth
            size="small"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.lighter
            }}
            disabled={
              getActionsDeleteDisabledStatus(params) || isActionDisabled(params)
            }
            onClick={() => handleDeleteButtonClick(params)}>
            Remove
          </LoadingButton>
        </Box>
      )
    }
  ];

  const totalSumRow = [
    {
      id: 'summary_record',
      sumValue: acceptedTransactions.totalPaymentAmountSum
    }
  ];

  const summaryColumns: GridColDef[] = [
    {
      field: 'PostingStatus',
      headerName: 'Status',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.id === 'summary_record' ? (
          <Typography sx={{fontWeight: 'bold'}}>{params.value}</Typography>
        ) : (
          params.value
        );
      }
    },
    {
      field: 'BatchCount',
      headerName: 'ERA Count',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.id === 'summary_record' ? (
          <Typography sx={{fontWeight: 'bold'}}>{params.value}</Typography>
        ) : (
          params.value
        );
      }
    },
    {
      field: 'Payment',
      headerName: 'Amount',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.id === 'summary_record' ? (
          <Typography sx={{fontWeight: 'bold'}}>
            {fCurrency(params.value)}
          </Typography>
        ) : (
          fCurrency(params.value)
        );
      }
    }
  ];

  const onFilterModelChange = (filterModel: any) => {
    if (filterModel) {
      const filterModelItems = filterModel.items.map((item: any) => {
        return {
          ...item,
          column_type: acceptedTransactionColumns.find(
            f => f.field === item.field
          )?.type
        };
      });
      setAcceptedTransactionsFilters({
        items: filterModelItems,
        logicOperator: filterModel.logicOperator
      });
    }
  };

  const CustomToolbar = () => {
    return (
      <Box sx={{display: 'flex', justifyContent: 'flex-start', padding: '4px'}}>
        <GridToolbarColumnsButton />
      </Box>
    );
  };

  const renderRemarksTimeline = (): ReactElement => {
    return (
      <>
        <DialogTitle
          sx={{
            textAlign: 'left',
            borderBottom: `solid 1px ${theme.palette.divider}`
          }}>
          Remarks History
        </DialogTitle>
        <DialogContent>
          <Box p={1}>
            <TextField
              label="Add Remark"
              fullWidth
              multiline
              rows={4}
              value={newRemark}
              onChange={e => setNewRemark(e.target.value)}
              sx={{marginBottom: '16px'}}
            />
            <Stack
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              spacing={2}>
              <LoadingButton
                type="button"
                variant="outlined"
                onClick={handleCloseDialog}
                sx={{
                  backgroundColor: theme.palette.primary.light
                }}>
                Close
              </LoadingButton>
              <LoadingButton
                loading={remarksAddButtonLoading}
                type="button"
                variant="contained"
                onClick={handleAddRemark}
                fullWidth>
                Add Remark
              </LoadingButton>
            </Stack>
            <Box
              mt={2}
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                padding: '10px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
              {remarksHistory.length === 0 ? (
                <Box m={2}>
                  {remarksHistoryLoading ? (
                    <LoadingScreen />
                  ) : (
                    <Typography
                      textAlign={'center'}
                      variant="body2"
                      color="textSecondary">
                      No remarks yet.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Timeline
                  position="right"
                  sx={{
                    [`& .${timelineOppositeContentClasses.root}`]: {
                      flex: 0.2
                    }
                  }}>
                  {remarksHistory.map((remark: any, index: number) => (
                    <TimelineItem key={index}>
                      <TimelineOppositeContent color="textSecondary">
                        Added by{' '}
                        <span
                          style={{
                            fontWeight: 'bold'
                          }}>
                          {remark.CreatedByName}
                        </span>{' '}
                        on{' '}
                        <span
                          style={{
                            fontStyle: 'italic',
                            width: '100%',
                            fontWeight: 'bold'
                          }}>
                          {moment(remark.BytepadCreatedDate).format(
                            'MM/DD/YYYY hh:mm A'
                          )}
                        </span>
                      </TimelineOppositeContent>

                      <TimelineSeparator>
                        <TimelineDot
                          variant="filled"
                          color="primary"
                          sx={{
                            backgroundColor: 'primary.main',
                            boxShadow: '0 0 10px rgba(6, 214, 160, 0.6)'
                          }}
                        />
                        <TimelineConnector sx={{backgroundColor: '#06D6A0'}} />
                      </TimelineSeparator>

                      <TimelineContent
                        sx={{
                          paddingLeft: '16px',
                          fontSize: '1rem',
                          color: '#333',
                          fontWeight: '400'
                        }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{fontStyle: 'italic'}}>
                          {`"${remark.Remarks}"`}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              )}
            </Box>
          </Box>
        </DialogContent>
      </>
    );
  };

  if (claimsTransactionId) {
    return (
      <BatchListing
        transactionId={claimsTransactionId}
        onClose={() => setClaimsTransactionId(null)}
        transactionInfo={acceptedTransactions?.transactions?.find(
          (tc: any) => tc.eraTransactionId === claimsTransactionId
        )}
        paymentPage={props?.paymentPage}
        paymentPageLabel={props?.paymentPageLabel}
      />
    );
  }

  if (batchLedgerId) {
    return (
      <BatchLedger
        batchId={batchLedgerId}
        onClose={() => setBatchLedgerId(null)}
        batchInfo={acceptedTransactions?.transactions?.find(
          (tc: any) => tc.id === batchLedgerId
        )}
        paymentPage={props?.paymentPage}
        paymentPageLabel={props?.paymentPageLabel}
        paymentConfig={props?.paymentConfig}
      />
    );
  }

  if (isPaymentEntry) {
    const currentBatchInfo = acceptedTransactions?.transactions.find(
      (tc: any) => tc.id === paymentEntryBatchId
    );
    const postingLevel = currentBatchInfo?.PostingLevel;
    return (
      <PaymentEntry
        patientAccountingConfig={props?.patientAccountingConfig}
        paymentConfig={props?.paymentConfig}
        onClosePaymentEntry={onClosePaymentEntry}
        batchId={paymentEntryBatchId}
        batchInfo={currentBatchInfo}
        paymentPageLabel={props?.paymentPageLabel}
        postingLevel={postingLevel}
      />
    );
  }

  const legacyAppsOptions = legacyApps.map((l: any) => {
    return {label: l.LegacyApp, value: l.LegacyApp};
  });
  return isPageLoading ? (
    <CircularProgressDiv>
      <LoadingScreen />
    </CircularProgressDiv>
  ) : (
    <PaymentAutoPostWrapper>
      <Stack mt={2}>
        <Box px={3} mt={1}>
          <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
            <Typography
              sx={{
                ...headerTypographySx
              }}>
              Upload ERA
            </Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              mt={2}
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(3, 1fr)'
              }}>
              <FormControl>
                <SelectInput
                  enableDefaultOption
                  options={legacyAppsOptions}
                  label="Legacy App"
                  placeholder="Select Legacy App"
                  name="legacyApp"
                  value={selectedLegacyApp || ''}
                  fullWidth
                  onChange={onChangeLegacyApp}
                  optionValueKey={'value'}
                  optionLabelKey={'label'}
                />
              </FormControl>
              <LoadingButton
                component="label"
                role={undefined}
                variant="outlined"
                tabIndex={-1}
                sx={{
                  borderWidth: '1.8px',
                  color: theme.palette.primary.dark
                }}
                startIcon={
                  <CloudUploadIcon sx={{color: theme.palette.primary.dark}} />
                }>
                Select ERA file
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleFileChange}
                  multiple={false}
                />
              </LoadingButton>
            </Box>
            {file ? (
              <Box mt={2} sx={{display: 'flex', alignItems: 'center'}}>
                <Typography sx={{fontWeight: 'bold', color: 'success.main'}}>
                  {'Selected ERA - '}
                </Typography>
                <Typography ml={1} sx={{fontWeight: 'bold'}}>
                  {file?.name}
                </Typography>
              </Box>
            ) : null}
            <Box
              sx={{
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}>
              <LoadingButton
                type="reset"
                variant="outlined"
                disabled={file || selectedLegacyApp ? false : true}
                onClick={() => {
                  setFile(null);
                  setSelectedLegacyApp('');
                }}
                sx={{
                  marginLeft: '24px',
                  borderRadius: '8px',
                  backgroundColor: theme.palette.primary.light
                }}>
                Reset
              </LoadingButton>
              <LoadingButton
                disabled={!file || !selectedLegacyApp}
                type="submit"
                variant="contained"
                loading={uploadingStatus}
                onClick={onUploadClick}
                sx={{
                  marginLeft: '12px',
                  backgroundColor: theme.palette.primary.dark
                }}
                loadingPosition="start">
                Upload
              </LoadingButton>
            </Box>
          </Card>
        </Box>
        <Box px={3} mt={1}>
          <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(3, 1fr)'
              }}>
              <DatePicker
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0000003b',
                    borderWidth: '1.8px'
                  }
                }}
                label="Deposit Date From"
                value={startDate}
                onChange={onChangeStartDate}
              />
              <DatePicker
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0000003b',
                    borderWidth: '1.8px'
                  }
                }}
                label="Deposit Date To"
                value={endDate}
                onChange={onChangeEndDate}
              />
            </Box>
            <Box
              sx={{
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}>
              <LoadingButton
                type="reset"
                onClick={onClearFilters}
                disabled={startDate || endDate ? false : true}
                variant="outlined"
                sx={{
                  marginLeft: '24px',
                  backgroundColor: theme.palette.primary.light
                }}>
                Reset
              </LoadingButton>
              <LoadingButton
                type="submit"
                onClick={onApplyButtonClick}
                disabled={!validDateStatus}
                variant="contained"
                sx={{
                  marginLeft: '12px',
                  backgroundColor: theme.palette.primary.dark
                }}>
                Search
              </LoadingButton>
            </Box>
            <DropDownPanel
              mt={2}
              title={'ERA Summary'}
              collapse={batchSummaryList.value}
              onCollapse={batchSummaryList.onToggle}>
              <Box
                px={3}
                sx={{
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  maxHeight: 'auto'
                }}>
                <StripedDataGrid
                  showColumnVerticalBorder
                  showCellVerticalBorder
                  autoHeight
                  disableRowGrouping
                  disableRowSelectionOnClick
                  disableColumnSelector
                  disableAggregation
                  checkboxSelection={false}
                  apiRef={gridApiRef}
                  loading={summaryTableLoading}
                  columns={summaryColumns}
                  rows={[...summaryData]}
                  rowThreshold={0}
                  rowHeight={37}
                  columnHeaderHeight={42}
                  hideFooter={true}
                  pagination={false}
                  paginationMode="server"
                  sortingMode="client"
                  sortingOrder={['desc', 'asc']}
                  filterMode="client"
                  getRowId={(row: any) => row.id || row.ID}
                  slotProps={{
                    filterPanel: {
                      sx: {
                        '& .MuiDataGrid-filterFormValueInput': {width: 'auto'}
                      }
                    }
                  }}
                  getRowClassName={(params: any) => {
                    return rowStyles(params);
                  }}
                  sx={getGridSx({
                    theme,
                    APP,
                    checkboxColor: props?.checkboxColor,
                    gridHeight: props?.gridHeight,
                    horizontalScroll: false
                  })}
                  slots={{
                    detailPanelExpandIcon: ExpandMoreOutlined,
                    detailPanelCollapseIcon: ExpandLessOutlined,
                    noRowsOverlay: () => (
                      <NoRowsOverlay
                        noDataFound
                        theme={theme}
                        loading={summaryTableLoading}
                      />
                    ),
                    noResultsOverlay: () => (
                      <NoRowsOverlay
                        noRowsFound
                        theme={theme}
                        loading={summaryTableLoading}
                      />
                    )
                  }}
                />
              </Box>
            </DropDownPanel>
          </Card>
        </Box>
        <Box px={3} mt={1}>
          <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
            <DropDownPanel
              title={'ERA List'}
              secondaryTitle={
                <FlexContainer>
                  <LoadingButton
                    type="submit"
                    disabled={acceptedTransactionsTableLoading}
                    loading={acceptedTransactionsTableLoading}
                    onClick={onRefreshTransactions}
                    sx={{
                      backgroundColor: theme.palette.primary.light
                    }}
                    loadingPosition="start"
                    variant="outlined">
                    Refresh
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    disabled={
                      !selectedClaimTransactionIds.length ? true : false
                    }
                    color="success"
                    loading={reviewTransactionsLoading}
                    onClick={onReviewTransactions}
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.dark
                    }}
                    loadingPosition="start"
                    variant="contained">
                    Reviewed & Completed
                  </LoadingButton>
                </FlexContainer>
              }
              collapse={eraList.value}
              onCollapse={eraList.onToggle}>
              <Box
                px={3}
                sx={{
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  maxHeight: 'auto'
                }}>
                <StripedDataGrid
                  showColumnVerticalBorder
                  showCellVerticalBorder
                  autoHeight
                  disableRowGrouping
                  disableRowSelectionOnClick
                  disableAggregation
                  checkboxSelection={false}
                  editMode="cell"
                  apiRef={gridApiRef}
                  loading={acceptedTransactionsTableLoading}
                  columns={acceptedTransactionColumns}
                  rows={acceptedTransactions.transactions}
                  rowThreshold={0}
                  rowHeight={37}
                  columnHeaderHeight={42}
                  hideFooter={false}
                  pagination={true}
                  paginationMode="server"
                  sortingMode="server"
                  sortingOrder={['desc', 'asc']}
                  filterMode="server"
                  onSortModelChange={onSortModelChange}
                  onFilterModelChange={onFilterModelChange}
                  processRowUpdate={processRowUpdate}
                  getCellClassName={getCellClassName}
                  isCellEditable={isCellEditable}
                  isRowSelectable={isRowSelectable}
                  onRowSelectionModelChange={onRowSelectionModelChange}
                  onProcessRowUpdateError={(error: any) =>
                    console.error('Error updating row:', error)
                  }
                  getRowId={(row: any) => row.id || row.ID}
                  slotProps={{
                    filterPanel: {
                      sx: {
                        '& .MuiDataGrid-filterFormValueInput': {width: 'auto'}
                      }
                    }
                  }}
                  getRowClassName={(params: any) => {
                    return rowStyles(params);
                  }}
                  sx={getGridSx({
                    theme,
                    APP,
                    checkboxColor: props?.checkboxColor,
                    gridHeight: props?.gridHeight,
                    horizontalScroll: true
                  })}
                  localeText={{
                    toolbarColumns: 'Manage Columns'
                  }}
                  slots={{
                    detailPanelExpandIcon: ExpandMoreOutlined,
                    detailPanelCollapseIcon: ExpandLessOutlined,
                    pagination: Pagination,
                    toolbar: CustomToolbar,
                    noRowsOverlay: () => (
                      <NoRowsOverlay
                        noDataFound
                        theme={theme}
                        loading={acceptedTransactionsTableLoading}
                      />
                    ),
                    noResultsOverlay: () => (
                      <NoRowsOverlay
                        noRowsFound
                        theme={theme}
                        loading={acceptedTransactionsTableLoading}
                      />
                    )
                  }}
                />
              </Box>
            </DropDownPanel>
          </Card>
        </Box>
      </Stack>
      {errorFileAlert && (
        <ActionModalDialog
          open={errorFileAlert}
          onClose={onErrorFileAlertClose}
          title="Invalid File"
          description="This document appears to be Blank or Corrupt. Do you still want to upload?"
          handleAgreeBtnClick={onUploadYesClick}
          handleCloseBtnClick={onErrorFileAlertClose}
          loader={false}
          yesLabelText={'Yes'}
          noLabelText={'No'}
          yesButtonColor={APP.PALETTE.ROLES.RED}
          noButtonColor={APP.PALETTE.ROLES.GRAY}
        />
      )}
      {openRemoveTransactionDialog && (
        <ActionModalDialog
          open={openRemoveTransactionDialog}
          onClose={onCancelRemoveTransactionClick}
          title="Do you want to delete this ERA?"
          description={transactionToRemove.fileName}
          handleAgreeBtnClick={onRemoveTransaction}
          handleCloseBtnClick={onCancelRemoveTransactionClick}
          loader={removeBatchLoading}
          yesLabelText={'Yes'}
          noLabelText={'No'}
          yesButtonColor={APP.PALETTE.ROLES.RED}
          noButtonColor={APP.PALETTE.ROLES.GRAY}
        />
      )}
      {displayPostERAModal && (
        <ActionModalDialog
          open={displayPostERAModal}
          onClose={onCancelRemovePostEra}
          title={`Do you want to post the ERA`}
          description={eraRecordToPost.fileName}
          handleAgreeBtnClick={postERA}
          handleCloseBtnClick={onCancelRemovePostEra}
          loader={postERALoading}
          yesLabelText={'Yes'}
          noLabelText={'No'}
          yesButtonColor={APP.PALETTE.ROLES.RED}
          noButtonColor={APP.PALETTE.ROLES.GRAY}
        />
      )}
      {isRemarksDialogOpen && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={isRemarksDialogOpen}
          onClose={handleCloseDialog}
          transitionDuration={{
            enter: theme.transitions.duration.shortest,
            exit: 0
          }}
          PaperProps={{
            sx: {
              overflow: 'unset'
            }
          }}
          sx={{
            [`& .${dialogClasses.container}`]: {
              alignItems: 'center',
              justifyContent: 'center'
            },
            '& .MuiStack-root': {
              borderRadius: '8px'
            }
          }}>
          {renderRemarksTimeline()}
        </Dialog>
      )}
    </PaymentAutoPostWrapper>
  );
};

export default PaymentAutoPost;
